package com.iregained.site.components.sections.product.sections

import androidx.compose.runtime.Composable
import com.iregained.site.components.sections.home.styles.PrimaryButtonStyle
import com.iregained.site.components.sections.patients.styles.HeaderSecondaryActionStyle
import com.iregained.site.components.widgets.SharedHeroActionLink
import com.iregained.site.components.widgets.SharedHeroWidget
import com.iregained.site.components.widgets.VideoFormat
import com.iregained.site.theme.toSitePalette
import com.iregained.site.utils.Res
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.animation
import com.varabyte.kobweb.compose.ui.modifiers.background
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.id
import com.varabyte.kobweb.compose.ui.modifiers.opacity
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.animation.Keyframes
import com.varabyte.kobweb.silk.style.animation.toAnimation
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import org.jetbrains.compose.web.css.AnimationTimingFunction
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.s
import org.jetbrains.compose.web.dom.H1
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.Sup

val HeroFadeInKeyframes = Keyframes {
    0.percent { Modifier.opacity(0) }
    100.percent { Modifier.opacity(1) }
}

@Composable
fun ProductHeroSection(data: ProductHeroSectionData = defaultProductHeroSectionData) {
    val targetId = data.sectionId?.takeIf { it.isNotBlank() } ?: "product-header-section"
    val palette = ColorMode.current.toSitePalette()

    if (data.headline.isNotBlank()) {
        Box(
            modifier = Modifier.id(targetId).fillMaxWidth().background(Colors.White).color(palette.custom.darkText).animation(
                HeroFadeInKeyframes.toAnimation(
                    duration = 1.5.s, timingFunction = AnimationTimingFunction.EaseInOut
                )
            )
        ) {
            SharedHeroWidget(
                overline = data.overline,
                headline = data.headline,
                description = data.description,
                imagePath = data.imagePath,
                heroVideoFormat = VideoFormat.SQUARE_1_1,
                actions = {
                    if (data.primaryCtaText.isNotBlank() && data.primaryCtaPath.isNotBlank()) {
                        SharedHeroActionLink(
                            text = data.primaryCtaText,
                            path = data.primaryCtaPath,
                            styleModifier = PrimaryButtonStyle.toModifier()
                        )
                    }
                    if (data.secondaryCtaText.isNotBlank() && data.secondaryCtaPath.isNotBlank()) {
                        SharedHeroActionLink(
                            text = data.secondaryCtaText,
                            path = data.secondaryCtaPath,
                            styleModifier = HeaderSecondaryActionStyle.toModifier()
                        )
                    }
                },
            )
        }
    }
}

/**
 * CMS IMAGE UPLOAD SPECS:
 * @field imagePath:
 * - Aspect Ratio: 4:3 (Landscape) or 1:1.
 * - Recommended Dimensions: 800x600px.
 * - Max File Size: 300KB.
 * - Behavior: Fills half screen on Desktop, scales gracefully on mobile (ObjectFit.Cover).
 */
data class ProductHeroSectionData(
    val sectionId: String? = null,
    val overline: String,
    val headline: String,
    val description: String,
    val primaryCtaText: String,
    val primaryCtaPath: String,
    val secondaryCtaText: String,
    val secondaryCtaPath: String,
    val imagePath: String
)

val defaultProductHeroSectionData = ProductHeroSectionData(
    sectionId = "product-header-section",
    overline = "Transformational neuro-rehabilitation for the hand!",
    headline = "The MyHand<sup>®</sup> System",
    description = "The MyHand<sup>®</sup> System is a targeted rehabilitation system for the hand. It is built on a clinically-proven, patented, AI-enabled, digital neurotherapeutics (DTx) platform that provides neuromechanical restitution to people living with hand function impairment as a result of a neurological or musculoskeletal condition.",
    primaryCtaText = "Request Demo",
    primaryCtaPath = "/contact",
    secondaryCtaText = "Learn More",
    secondaryCtaPath = "/resources#resources-brochure-download",
    imagePath = Res.Images.ProductPage.OVERVIEW_BG
)
